// Composer Studio — 템플릿 **로드**(DB → 편집 상태) 정규화
//
// 저장 경로(toUpdateBody, ./templateUpdateBody)의 **짝**이다. 두 함수의 필드 목록은 대칭이어야
// 하고(한쪽에만 있는 필드는 유령: 저장돼도 안 보임 / 소실: 로드 누락 상태로 저장해 DB를 비움),
// 그 대칭을 지금은 사람이 아니라 spec이 지킨다(./templateFieldSymmetry.test.ts).
//
// EditorPage에서 끌어낸 이유가 바로 그 spec이다 — 규약은 주석으로만 못박혀 있었고 두 목록이
// 다른 파일에 있어 대조할 방법이 없었다. 드리프트의 결과가 조용한 DB 소실이라, 규약을 실행
// 가능한 검사로 바꾼다.
import type { ComposerNode, ReportTemplate } from '@safetyhub/shared';
import { stripComputeDefFields } from './dataDefJoin';
import { migrateLegacyMergeFields } from './mergeFieldsMigrate';
import { normalizeDataDefFieldRefs } from './dataDefFieldsNormalize';

/**
 * 트리 전 노드에 layout 기본값({}) 보장(로드 직후 1회 mutate).
 * DB 템플릿은 외부 JSON이라 layout 누락 노드가 유입될 수 있는데, ComposerNodeBase.layout은
 * 필수라 PropertyPanel(alignH)/NodeEdgeResizeOverlay(height)가 선택 시점에 크래시한다(2026-07-03 실사고
 * — 조립 스크립트 산출 노드 25개 layout 누락). 로드 경계 한 곳에서 정규화한다.
 */
export function ensureNodeLayouts(nodes: ComposerNode[]): ComposerNode[] {
  const visit = (n: ComposerNode): void => {
    if (!n.layout) (n as { layout: ComposerNode['layout'] }).layout = {};
    if ('children' in n && Array.isArray(n.children)) n.children.forEach(visit);
    if (n.kind === 'branch') for (const s of n.branches) s.children.forEach(visit);
    if (n.kind === 'repeat') visit(n.template);
    if (n.kind === 'table') for (const c of n.cells) if (c.content) c.content.forEach(visit);
  };
  nodes.forEach(visit);
  return nodes;
}

/** StoredReportTemplate(id/enabled/createdAt/updatedAt 포함)에서 순수 ReportTemplate 필드만 추출 — 편집 상태에 저장 메타가 누출되지 않게. */
export function toEditableTemplate(raw: ReportTemplate): ReportTemplate {
  // 레거시 join-by-key mergeFields 자동 이행 — stripComputeDefFields와 같은 "로드 정규화 →
  // 다음 저장 때 DB 정리" 패턴. body/composerWidgetTree/dataDefs가 함께 변환된 t에서
  // 추출돼야 정합(한쪽만 이행되면 그 셀만 빈 값).
  const migrated = migrateLegacyMergeFields(raw);
  // 내장 데이터셋 표 위젯 칼럼 사본의 참조화(P3) — 사본==정본이면 사본 삭제(정본 live 참조로
  // 전환, 렌더 결과 불변). 드리프트·정본 없음 사본은 보존(위젯 칼럼 패널이 배너로 안내).
  // 같은 "로드 정규화 → 다음 저장 때 DB 정리" 패턴.
  const t = normalizeDataDefFieldRefs(migrated).template;
  return {
    key: t.key,
    name: t.name,
    // 제목 규칙(2026-08-17) — 로드/저장(toUpdateBody) 대칭 필수(한쪽만 있으면 유령/소실).
    titleTemplate: t.titleTemplate,
    schemaVersion: t.schemaVersion,
    systemType: t.systemType,
    pluginKey: t.pluginKey,
    // 진입 전 사용자 입력 스위치 — 로드/저장(toUpdateBody) 대칭 필수. 누락 시 체크박스가 항상
    // off로 보이고 토글이 저장되지 않는 유령 상태(2026-07-25 감사 P1 실결함).
    entryUserInput: t.entryUserInput,
    page: t.page,
    body: ensureNodeLayouts(t.body),
    // ⚠️ ReportTemplate의 편집 대상 필드는 모두 보존해야 한다. dataDefs(N2-A)/approvalLineMeta(P2a)를
    //    누락하면 로드 시 에디터에서 사라지고, 그 상태로 저장하면 DB 값까지 지워진다(데이터 손실).
    //    단 조립(compute) 데이터셋의 잔존 칼럼 선언은 로드 시 정규화로 비운다(자동 노출 전환,
    //    2026-07-26) — 다음 저장 때 DB도 함께 정리된다.
    dataDefs: stripComputeDefFields(t.dataDefs),
    // 조립형 데이터 소스(Phase B) — dataDefs와 동일 이유로 보존 필수(누락 시 로드/저장서 소실).
    dataSources: t.dataSources,
    // 카탈로그 파라미터 지정(2026-08-06 결정 개정 1) — 동일 이유로 보존 필수(누락 시 로드/저장서 소실).
    //   ⚠️ P2 시점엔 편집 UI가 없어(P3) 이 왕복이 유일한 보존 경로다 — 빠지면 seed·수동 지정이
    //   에디터 저장 한 번에 조용히 사라진다.
    catalogParams: t.catalogParams,
    approvalLineMeta: t.approvalLineMeta,
    approvalPlacement: t.approvalPlacement,
    // 작성 페이지 위젯 트리(2026-07-08 결정) — 동일 이유로 보존 필수(누락 시 로드/저장서 소실).
    composerWidgetTree: t.composerWidgetTree,
    // 작성 폼 chrome 구성(2026-07-09 결정) — 동일 이유로 보존 필수(누락 시 로드/저장서 소실).
    composeChrome: t.composeChrome,
    // 상세 페이지 chrome 상태별 표시 구성(2026-07-21 결정) — 동일 이유로 보존 필수(누락 시 로드/저장서 소실).
    detailChrome: t.detailChrome,
    // 위임 작성 메타(2026-07-06 결정) — 동일 이유로 보존 필수(누락 시 로드/저장서 소실).
    delegationMeta: t.delegationMeta,
    // 양식 전역 변수(2026-07-07 결정) — 동일 이유로 보존 필수(누락 시 로드/저장서 소실).
    variables: t.variables,
    // 노출·중복 정책(2026-07-26) — toUpdateBody(저장)와 대칭(한쪽만 있으면 유령/소실).
    policy: t.policy,
    // 양식 업로드 초안 메타(2026-07-27) — 반복 영역 제안(repeatHints)의 보존소. 로드 누락 시
    // 배지가 안 뜨고, 그 상태로 저장하면 toUpdateBody의 null 해제가 DB 값을 지운다(소실).
    importMeta: t.importMeta,
    // 모사 데이터 프리셋(2026-09-03 결정) — toUpdateBody(저장)와 대칭(한쪽만 있으면 유령/소실).
    simulationPresets: t.simulationPresets,
    // 기본 모사 프리셋 id(2026-09-04 결정) — 로드 시마다 그 프리셋을 미리보기 모사에 반영하는
    // 축. toUpdateBody(저장)와 대칭 필수(누락 시 기본 지정이 저장 한 번에 조용히 소실).
    defaultSimulationPresetId: t.defaultSimulationPresetId,
  };
}
